import type {CSSProperties} from "react";

/**
 * Gabarits partagés par les cartes d'état (`AddressCloseMatch`,
 * `ManualPinFrozen`) — la mise en page est commune, seule la palette
 * (violet « proche trouvée » vs teinte action « doute levé ») appartient à
 * chaque composant d'état. `AddressNotFound` (#799, plus bas dans ce fichier)
 * est une famille visuelle SÉPARÉE — reprise telle quelle du pattern
 * pré-existant, pas de ce gabarit.
 */

export const STATUS_CARD_BASE_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  borderRadius: "12px",
  padding: "10px 12px",
  marginBottom: "8px",
};

export const STATUS_ICON_BASE_STYLE: CSSProperties = {
  width: "26px",
  height: "26px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: "12px",
};

export const STATUS_TITLE_STYLE: CSSProperties = {
  fontWeight: 700,
  fontSize: "13px",
  color: "#2c2c34",
};

/**
 * #6c757d : contraste ~4,7:1 sur fond blanc (seuil WCAG AA 4,5:1) — ne pas
 * repasser sur un gris plus clair type #8a8a94 (#1007), sous le seuil (~3,5:1).
 */
export const STATUS_LABEL_STYLE: CSSProperties = {
  fontSize: "12px",
  color: "#6c757d",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const STATUS_ACTIONS_ROW_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  paddingLeft: "36px",
};

// Teinte action apaisée — états « doute levé » (confirmé, pin manuel).
export const RESOLVED_CARD_TINT: CSSProperties = {
  background: "rgba(var(--mt-color-action-rgb), 0.05)",
  border: "1px solid rgba(var(--mt-color-action-rgb), 0.25)",
};

export const RESOLVED_ICON_TINT: CSSProperties = {
  background: "rgba(var(--mt-color-action-rgb), 0.14)",
  color: "var(--mt-color-action)",
};

/** Variante multi-ligne de `STATUS_LABEL_STYLE` (#980) : le libellé du callout « Adresse proche trouvée » porte un segment surligné, il ne peut pas être tronqué sur une ligne. */
export const STATUS_LABEL_WRAP_STYLE: CSSProperties = {
  fontSize: "12px",
  color: "#6c757d",
  whiteSpace: "normal",
  lineHeight: 1.4,
};

/** Bouton pleine largeur (« Remplacer », « Positionner sur la carte » — #980, mock-up validé). */
export const STATUS_FULL_WIDTH_BUTTON_STYLE: CSSProperties = {width: "100%"};

/** Segment divergent surligné dans le libellé du callout « Adresse proche trouvée » (#980, mock-up validé). */
export const DIFF_MARK_STYLE: CSSProperties = {
  background: "rgba(230, 126, 34, 0.18)",
  color: "#b9660b",
  fontWeight: 700,
  padding: "0 2px",
  borderRadius: "3px",
};

/**
 * Famille visuelle de la carte « Adresse introuvable » (#799) — reprise telle
 * quelle de `AddressStep.scss` (pré-#980, `packages-web/rdv-public`) :
 * gabarit et palette (ambré) distincts de `STATUS_CARD_BASE_STYLE`, ne pas
 * les fusionner (le mock-up validé les distingue explicitement).
 */
export const VERIFY_CARD_STYLE: CSSProperties = {
  background: "#fbfaff",
  border: "1px solid #ece7f7",
  borderRadius: "12px",
  padding: "14px 16px",
  marginBottom: "8px",
};

export const VERIFY_CARD_HEADER_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  marginBottom: "12px",
};

export const VERIFY_CARD_ICON_STYLE: CSSProperties = {
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  background: "rgba(214, 145, 20, 0.12)",
  color: "#b8791a",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const VERIFY_CARD_TITLE_STYLE: CSSProperties = {
  fontWeight: 700,
  fontSize: "0.92rem",
  color: "#2c2c34",
  lineHeight: 1.25,
};

export const VERIFY_CARD_SUBTITLE_STYLE: CSSProperties = {
  fontSize: "0.8rem",
  color: "#6c757d",
  marginTop: "1px",
};

export const VERIFY_CARD_LIST_STYLE: CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "7px",
};

export const VERIFY_CARD_LIST_ITEM_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "9px",
  fontSize: "0.85rem",
  color: "#45454e",
};

export const VERIFY_CARD_BULLET_STYLE: CSSProperties = {
  width: "18px",
  height: "18px",
  borderRadius: "50%",
  background: "rgba(var(--mt-color-action-rgb), 0.1)",
  color: "var(--mt-color-action)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
